import React, { memo, useCallback, useState } from "react";
import { generatePath } from "react-router";

import dayjs from "@/lib/dayjs";
import { StatusRefund, type RefundRequestList_RefundRequestTopPageQuery } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent } from "@/components/ui/dialog/dialog";
import Article from "@/components/ui/icon/article";
import OpenInNew from "@/components/ui/icon/open-in-new";
import { Label } from "@/components/ui/label/label";
import { TableCell, TableRow } from "@/components/ui/table/table";

type Props = {
	row: RefundRequestList_RefundRequestTopPageQuery["refundRequestList"]["list"][number];
};

const FORMAT_DATE_TEMPLATE = "YYYY-MM-DD";

const RefundRequestStatus = memo(({ status }: { status: StatusRefund }) => {
	return (
		<>
			{status === StatusRefund.Pending ? (
				<Label text="審査中" variant="green" />
			) : status === StatusRefund.Approval ? (
				<Label text="承認" variant="blue" />
			) : (
				<Label text="非承認" variant="required" />
			)}
		</>
	);
});

const KVItem = memo(({ label, value }: { label: string | React.ReactNode; value: string }) => {
	return (
		<div className="space-y-1">
			{typeof label === "string" ? <p className="text-sm font-semibold">{label}</p> : <>{label}</>}
			<p className="break-all">{value}</p>
		</div>
	);
});

export const ManagementRefundRequestTopTableBodyRow = memo(
	({
		row: {
			status,
			refundRequestAmount,
			guestName,
			latestUpdatedAt,
			refundRequestReason,
			refundRejectReason,
			hotelID,
			reservationID,
			dateCin,
			dateCout,
		},
	}: Props) => {
		const refundRequestAmountText = `${refundRequestAmount.toLocaleString()}円`;
		const formattedLatestUpdatedAt = dayjs(latestUpdatedAt).format(FORMAT_DATE_TEMPLATE);
		const isPending = status === StatusRefund.Pending;
		const isApproval = status === StatusRefund.Approval;
		const isPendingOrApproval = isPending || isApproval;

		const formattedDateCIn = dayjs(dateCin).format(FORMAT_DATE_TEMPLATE);
		const formattedDateCOut = dayjs(dateCout).format(FORMAT_DATE_TEMPLATE);

		const [open, setOpen] = useState(false);
		const activateModal = useCallback(() => setOpen(true), []);
		const handleOpenChange = useCallback((input: boolean) => {
			setOpen(input);
		}, []);

		const handleClickOpenRefundDetailModalButton = useCallback(() => {
			activateModal();
		}, [activateModal]);

		const handleClickNavigateToReservationDetailPageButton = useCallback(() => {
			const path = generatePath(PATHS.HOTEL.MANAGEMENT.RESERVATION.DETAIL, { hotelID, reservationID });
			window.open(path, "_blank");
		}, [hotelID, reservationID]);

		return (
			<>
				<Dialog open={open} onOpenChange={handleOpenChange}>
					<DialogContent className="max-w-3xl overflow-y-scroll">
						<div className="space-y-6">
							<div>
								<RefundRequestStatus status={status} />
							</div>

							<div className="grid grid-cols-2 gap-x-4">
								<div className="col-span-1 space-y-4">
									<KVItem label="返金金額" value={refundRequestAmountText} />
									<KVItem label="予約ID" value={reservationID} />
									<KVItem label="ゲストの名前" value={guestName} />
									<KVItem label="日程" value={`${formattedDateCIn} 〜 ${formattedDateCOut}`} />
									<KVItem label="最終更新日" value={formattedLatestUpdatedAt} />
								</div>

								<div className="col-span-1">
									<KVItem
										label={
											isPendingOrApproval ? (
												<Label text="リクエスト理由" variant="green" size="xs" />
											) : (
												<Label text="非承認理由" variant="required" size="xs" />
											)
										}
										value={isPendingOrApproval ? refundRequestReason : (refundRejectReason ?? "")}
									/>
								</div>
							</div>
						</div>
					</DialogContent>
				</Dialog>

				<TableRow>
					<TableCell>
						<RefundRequestStatus status={status} />
					</TableCell>

					<TableCell>{refundRequestAmountText}</TableCell>

					<TableCell>
						<p className="line-clamp-3 break-all">{guestName}</p>
					</TableCell>

					<TableCell>{formattedLatestUpdatedAt}</TableCell>

					<TableCell>
						{isPendingOrApproval ? (
							<div className="space-y-3">
								<Label text="リクエスト理由" variant="green" size="xs" />
								<p className="line-clamp-5 break-all">{refundRequestReason}</p>
							</div>
						) : (
							<div className="space-y-3">
								<Label text="非承認理由" variant="required" size="xs" />
								<p className="line-clamp-5 break-all">{refundRejectReason}</p>
							</div>
						)}
					</TableCell>

					<TableCell>
						<Button
							onClick={handleClickOpenRefundDetailModalButton}
							tooltip="返金処理詳細"
							type="button"
							variant="input-ish"
							size="icon"
						>
							<Article size="sm" />
						</Button>
					</TableCell>

					<TableCell>
						<Button
							onClick={handleClickNavigateToReservationDetailPageButton}
							tooltip="予約詳細"
							type="button"
							variant="input-ish"
							size="icon"
						>
							<OpenInNew size="sm" />
						</Button>
					</TableCell>
				</TableRow>
			</>
		);
	},
);

ManagementRefundRequestTopTableBodyRow.displayName = "ManagementRefundRequestTopTableBodyRow";
